import { useMutation } from '@apollo/client';
import { useTranslations } from '@vocab/react';
import {
  Actions,
  Button,
  Column,
  Columns,
  Dialog,
  Dropdown,
  FieldMessage,
  IconDelete,
  IconResume,
  type Inline,
  Stack,
  Text,
} from 'braid-design-system';
import { type ComponentProps, useEffect, useState } from 'react';

import type { Resume } from 'src/__generated__/graphql';
import {
  ActionOrigin,
  Page,
  PageSection,
  UpdateType,
  useTrackingClient,
} from 'src/context/tracking';
import { useProfileAnalytic } from 'src/hooks/useProfileAnalytic';
import { logError } from 'src/modules/error-logger/browserLog';
import { formatDateShort, getOperationNameFromQuery } from 'src/modules/utils';

import { DELETE_RESUME } from '../DocumentDeleteDialog/delete-resume.graphql';
import { DocumentDownloadButton } from '../DocumentDownloadButton/DocumentDownloadButton';

import translations from './.vocab';

type ReactNodeNoStrings = ComponentProps<typeof Inline>['children'];

type DialogProps = ComponentProps<typeof Dialog> & {
  documentList: Resume[];
  onDeleted: (value: string) => void;
};
type Props = Pick<
  DialogProps,
  'open' | 'onClose' | 'documentList' | 'onDeleted'
>;

export const DocumentLimitExceededDialog = ({
  open,
  onClose,
  documentList = [],
  onDeleted,
}: Props) => {
  const client = useTrackingClient();
  const { t } = useTranslations(translations);
  const {
    profileApproachabilityStatus,
    profileSharedStatus,
    profileVisibilityLevel,
  } = useProfileAnalytic();
  const [value, setValue] = useState('');
  const [errorMessage, setErrorMessage] = useState<ReactNodeNoStrings>(null);

  // TODO: Expand to other document types
  const [deleteResume] = useMutation(DELETE_RESUME);

  const options = documentList.map((doc) => (
    <option key={doc.id} value={doc.id}>
      {`${formatDateShort(doc.createdDateUtc)} - ${doc.fileMetadata?.name}`}
    </option>
  ));

  const confirmDeleteResume = async () => {
    if (!Boolean(value)) {
      setErrorMessage(<>{t('Please select a resume')}</>);
      return;
    }

    const hasSpi = documentList.find((doc) => doc.id === value)?.fileMetadata
      .sensitiveDataInfo?.isDetected;

    try {
      await deleteResume({
        variables: {
          input: {
            id: value,
          },
        },
      });
      client.trackLink({
        link: 'profile_update_succeeded',
        currentPage: Page.DOCUMENT_PAGE,
        actionOrigin: ActionOrigin.APPLY,
        updateType: UpdateType.DELETE,
        currentPageSection: PageSection.RESUME,
        profileApproachabilityStatus,
        profileSharedStatus,
        profileVisibilityLevel,
        resumeHasSensitivePersonalInfo: Boolean(hasSpi),
      });
      onDeleted(value);
    } catch (error: any) {
      setErrorMessage(<>{t('Could not delete resume')}</>);
      logError(
        {
          ...error,
          type: 'delete-resume',
        },
        {
          error,
          stack: error.stack,
          resumeId: value,
          operationName: getOperationNameFromQuery(DELETE_RESUME),
        },
      );
    }
    setValue('');
  };

  useEffect(() => {
    if (open) {
      setErrorMessage(null);
      setValue('');

      client.trackLink({
        link: 'resume_limit_modal_viewed',
        currentPage: Page.DOCUMENT_PAGE,
      });
    }
  }, [client, open]);

  useEffect(() => {
    if (Boolean(value)) {
      setErrorMessage(null);
    }
  }, [value]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      id="resume-limit-exceeded-warning"
      title={t('resume limit reached')}
      closeLabel={t('Dismiss')}
    >
      <Stack space="medium">
        <Text data={{ testid: '10-resume-limit-text' }}>
          {t('Please select a resume to delete')}
        </Text>
        <Stack space="medium">
          <Columns space="small" alignY="center">
            <Column>
              <Dropdown
                id="docLimitExceededDropdown"
                label=""
                placeholder={t('Please select a resume')}
                value={value}
                icon={<IconResume />}
                onChange={(e) => {
                  setValue(e.currentTarget.value);
                }}
              >
                {options}
              </Dropdown>
            </Column>
            {Boolean(value) ? (
              <Column width="content">
                <DocumentDownloadButton id="downloadResume" resumeId={value} />
              </Column>
            ) : null}
          </Columns>
          <Actions>
            <Button
              onClick={confirmDeleteResume}
              tone="critical"
              icon={<IconDelete />}
              data={{ automation: '10-resume-delete' }}
            >
              {t('Delete')}
            </Button>
          </Actions>
          {errorMessage && (
            <FieldMessage id="0" message={errorMessage} tone="critical" />
          )}
        </Stack>
      </Stack>
    </Dialog>
  );
};
